import { useQuery } from "@tanstack/react-query";
import ErrorMessage from "components/common/base/ErrorMessage";
import Loading from "components/common/base/Loading";
import { useStoreContext } from "context/StoreProvider";
//import { t } from 'i18next'
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collectionsString, endpoints } from "utils";

import Node from "./Node";
import { AdmCollectionResponse } from "./types";

const HierarchyAdm = () => {
  const navigate = useNavigate();
  const scrollRef = useRef<Record<string, HTMLDivElement | null>>({});

  const { state, actions } = useStoreContext();

  const { code } = state;
  const { updateCodeValue } = actions;

  const [fetchError, setFetchError] = useState<string>("");

  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);

  /* Kan ikke bruke fetchApi, fordi hierarkiet har en ektra condition om å hente root dersom den får error */
  const fetchHierarchy = async () => {
    const res = await fetch(endpoints.apiAdmCollectionsEndpointDev);

    if (!res.ok && res.status !== 404) {
      setFetchError(res.statusText);
    } else {
      return await res.json();
    }
  };
  const {
    data: hierarchyAdm,
    isLoading,
    isError
  } = useQuery<AdmCollectionResponse[], Error>({
    queryKey: [`adm-hierarchy`],
    queryFn: fetchHierarchy,
    retry: false
  });

  useEffect(() => {
    if (code) {
      setExpandedNodes((prevExpandedNodes) => {
        return prevExpandedNodes.includes(code)
          ? prevExpandedNodes
          : [...prevExpandedNodes, code];
      });

      // If hierarchyAdm is loaded, find the parent of the node with id === code and add it to expandedNodes
      if (hierarchyAdm) {
        for (const parent of hierarchyAdm) {
          const hasActiveCode = parent.members.some(
            (child) => child.id === code
          );

          if (hasActiveCode && !expandedNodes.includes(parent.id)) {
            setExpandedNodes((prevExpandedNodes) => [
              ...prevExpandedNodes,
              parent.id
            ]);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, hierarchyAdm]);

  const getUpdatedExpandedNodes = (
    prevExpandedNodes: string[],
    parentId: string
    // eslint-disable-next-line unicorn/consistent-function-scoping
  ) =>
    prevExpandedNodes.includes(parentId)
      ? // If the node is already expanded, remove its ID from selectedParentIds
        prevExpandedNodes.filter((id) => id !== parentId)
      : // If the node is not expanded, add its ID to selectedParentIds
        [...prevExpandedNodes, parentId];

  const handleExpand = (parent: AdmCollectionResponse) => () => {
    setExpandedNodes((prevExpandedNodes) =>
      getUpdatedExpandedNodes(prevExpandedNodes, parent.id)
    );
  };

  const handleNavigate = (item: AdmCollectionResponse) => {
    updateCodeValue(item.id as string);
    navigate(`${collectionsString}/${item.id}`);
  };

  useLayoutEffect(() => {
    const scrollToElement = () => {
      const element = scrollRef.current[code as string];
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    scrollToElement();
  }, [code]);

  return (
    <aside id="adm-hierarchy">
      <div style={{ paddingBottom: "4rem" }}>
        {isError && <ErrorMessage errorResponse={fetchError} />}
        {isLoading && <Loading />}
        {Array.isArray(hierarchyAdm) &&
          hierarchyAdm
            .toSorted(function (a: { name: string }, b: { name: string }) {
              if (a?.name < b?.name) {
                return -1;
              }
              if (a?.name > b?.name) {
                return 1;
              }
              return 0;
            })
            .map((parent: AdmCollectionResponse) => {
              const level = 0;

              const hasActiveCode = parent.members.some(
                (child) => child.id === code
              );
              return (
                <Node
                  key={parent.id}
                  item={parent}
                  isActive={hasActiveCode}
                  isExpanded={expandedNodes.includes(parent.id)}
                  handleNavigate={handleExpand(parent)}
                  handleExpand={handleExpand(parent)}
                  level={level}
                  innerRef={(ref: HTMLDivElement | null) => {
                    scrollRef.current[parent.id] = ref;
                  }}
                >
                  {expandedNodes.includes(parent.id) &&
                    parent.members &&
                    parent.members
                      .toSorted(
                        (a: { id: string }, b: { id: string }) =>
                          Number.parseInt(a.id) - Number.parseInt(b.id)
                      )
                      .map((child) => {
                        return (
                          <Node
                            key={child.id}
                            item={child}
                            isExpanded={child.id === code}
                            isActive={child.id === code}
                            handleNavigate={() => handleNavigate(child)}
                            level={level + 1}
                            innerRef={(ref: HTMLDivElement | null) => {
                              scrollRef.current[child.id] = ref;
                            }}
                          />
                        );
                      })}
                </Node>
              );
            })}
      </div>
    </aside>
  );
};

export default HierarchyAdm;
